import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

const STATE_KEY = 'hinote-welcome-popup-state';
const SESSION_KEY = 'hinote-welcome-popup-session';
const GEO_DISMISSED_KEY = 'hinote-geolocation-popup-dismissed';

const FREQUENCY_MS = {
  '1_day': 24 * 60 * 60 * 1000,
  '7_day': 7 * 24 * 60 * 60 * 1000,
  '14_day': 14 * 24 * 60 * 60 * 1000,
  '30_day': 30 * 24 * 60 * 60 * 1000,
  '90_day': 90 * 24 * 60 * 60 * 1000,
};

class WelcomePopupComponent extends Component {
  requiredRefs = ['dialogComponent'];

  #abortController;
  #currentStep = 1;
  #triggerSource = null;
  #closeListener;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Dialog close event from the dialog-component
    this.refs.dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
      { signal }
    );

    // Form submit via event delegation — robust against re-renders
    this.addEventListener(
      'submit',
      (event) => {
        if (event.target?.classList?.contains('welcome-popup__form')) {
          this.#handleSubmit(event);
        }
      },
      { signal }
    );

    // Email input feedback
    this.addEventListener(
      'input',
      (event) => {
        if (event.target?.id === 'WelcomePopupEmail') this.#handleEmailInput();
      },
      { signal }
    );

    // Native Escape key on dialog
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dismissPopup();
      }
    }, { signal });

    // Native click delegation as a robust fallback for the framework's on:click
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('.welcome-popup__close')) {
        event.preventDefault();
        this.dismissPopup();
        return;
      }
      if (target.closest('.welcome-popup__overlay')) {
        event.preventDefault();
        this.dismissPopup();
        return;
      }
      const claim = target.closest('.welcome-popup__claim');
      if (claim) {
        // Don't preventDefault — let the link navigate. Just emit GA4.
        const destination = claim.dataset.destinationUrl || claim.getAttribute('href') || '';
        this.#emitGA4('discount_claim_click', { destination_url: destination });
      }
    }, { signal });

    if (this.#isDevMode) {
      console.info(
        '[WelcomePopup] DEV MODE on — bypassing all gates and opening immediately.\n' +
        '  Step: ' + (this.dataset.devStep || 'form') + '\n' +
        '  Turn this off in the customizer (Welcome popup → Developer → Dev mode) before publishing.'
      );
      requestAnimationFrame(() => {
        this.#open('dev_mode');
        if (this.dataset.devStep === 'confirmation') {
          this.#goToConfirmation();
        }
      });
      return;
    }

    if (this.isDesignMode) {
      // In editor: render-only, no auto-fire
      return;
    }
    if (!this.#checkGates()) {
      this.#logSuppressionHelp();
      return;
    }

    this.#wireTriggers();
  }

  get #isDevMode() {
    return this.dataset.devMode === 'true';
  }

  #logSuppressionHelp() {
    const state = this.#readState();
    if (state.signedUp || (state.suppressUntil && new Date(state.suppressUntil) > new Date()) || this.#isSessionDismissed()) {
      console.info(
        '[WelcomePopup] Suppressed by saved state. To reset for testing, run:\n' +
        "  localStorage.removeItem('hinote-welcome-popup-state'); sessionStorage.removeItem('hinote-welcome-popup-session'); location.reload();\n" +
        'Or enable Dev mode in the customizer (Welcome popup → Developer).'
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  // Public actions ------------------------------------------------

  dismissPopup = async () => {
    this.#emitGA4('popup_dismissed');
    this.#persistDismissal();
    try {
      await this.refs.dialogComponent.closeDialog();
    } catch (err) {
      console.error('[WelcomePopup] closeDialog failed', err);
    }
  };

  handleClaimClick = (event) => {
    const link = event.currentTarget;
    const destination = link?.dataset?.destinationUrl || link?.getAttribute?.('href') || '';
    this.#emitGA4('discount_claim_click', { destination_url: destination });
    // Allow native navigation to proceed.
  };

  // Gates ---------------------------------------------------------

  #checkGates() {
    if (this.#isExcludedPath()) {
      console.debug('[WelcomePopup] suppressed: excluded path');
      return false;
    }
    if (this.#isExcludedHash()) {
      console.debug('[WelcomePopup] suppressed: excluded hash');
      return false;
    }
    if (this.#isSessionDismissed()) {
      console.debug('[WelcomePopup] suppressed: session dismissed');
      return false;
    }
    if (this.#isPersistentlySuppressed()) {
      console.debug('[WelcomePopup] suppressed: persistent state');
      return false;
    }
    if (this.#shouldYieldToGeoPopup()) {
      console.debug('[WelcomePopup] suppressed: geo popup precedence');
      return false;
    }
    return true;
  }

  #wireTriggers() {
    const signal = this.#abortController.signal;

    if (this.dataset.enableImmediate === 'true') {
      requestAnimationFrame(() => this.#open('immediate'));
      return;
    }

    if (this.dataset.enableTimeout === 'true') {
      const ms = Number(this.dataset.timeoutSeconds || 5) * 1000;
      const timeoutId = setTimeout(() => this.#open('timeout'), ms);
      signal.addEventListener('abort', () => clearTimeout(timeoutId));
    }

    if (this.dataset.enableScroll === 'true') {
      const target = Number(this.dataset.scrollPercent || 50) / 100;
      const onScroll = () => {
        const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
        if (window.scrollY / max >= target) this.#open('scroll');
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal });
    }

    if (this.dataset.enableExitIntent === 'true' && !window.matchMedia('(pointer: coarse)').matches) {
      const onMouseLeave = (event) => {
        if (event.clientY <= 0) this.#open('exit_intent');
      };
      document.documentElement.addEventListener('mouseleave', onMouseLeave, { signal });
    }
  }

  #open(triggerSource) {
    if (this.#triggerSource) return;
    this.#triggerSource = triggerSource;

    this.classList.add('welcome-popup--open');
    this.refs.dialogComponent.showDialog();
    requestAnimationFrame(() => {
      this.#closeButton?.focus();
    });

    this.#emitGA4('popup_view', { trigger_source: triggerSource });
  }

  #handleDialogClose = () => {
    this.classList.remove('welcome-popup--open');
    if (this.#currentStep === 1) {
      this.#persistDismissal();
    }
  };

  // Form submission -----------------------------------------------

  #handleSubmit = async (event) => {
    event.preventDefault();
    console.debug('[WelcomePopup] form submitted');

    const input = this.#emailInput;
    const submit = this.#submitButton;
    if (!input || !submit) {
      console.warn('[WelcomePopup] missing input or submit button');
      return;
    }

    if (!input.checkValidity()) {
      this.#showError(input.validity.valueMissing ? this.#errorRequired : this.#errorInvalid);
      return;
    }

    this.#clearError();
    this.#setFormState('submitting');
    submit.setAttribute('disabled', 'disabled');
    submit.setAttribute('aria-busy', 'true');
    this.#emitGA4('popup_signup_submitted');

    try {
      const email = input.value.trim();
      await this.#submitToKlaviyo(email);
      input.value = '';
      this.#identifyInKlaviyo(email);
      this.#setFormState('success');
      submit.removeAttribute('aria-busy');
      this.#emitGA4('popup_signup_success');
      this.#persistSignup();
      this.#goToConfirmation();
    } catch (error) {
      console.error('[WelcomePopup] Klaviyo submit failed', error);
      submit.removeAttribute('disabled');
      submit.removeAttribute('aria-busy');
      this.#showError(this.#errorInvalid);
      this.#setFormState('error');
    }
  };

  #handleEmailInput = () => {
    if (this.#emailInput?.checkValidity()) this.#clearError();
  };

  async #submitToKlaviyo(email) {
    const publicKey = this.dataset.klaviyoPublicKey;
    const listId = this.dataset.klaviyoListId;
    console.debug('[WelcomePopup] submitting to Klaviyo', { email, hasKey: !!publicKey, hasList: !!listId });

    if (!publicKey || !listId) {
      throw new Error('Missing Klaviyo public key or list ID');
    }

    const country = (this.dataset.countryCode || '').trim().toUpperCase();
    const properties = {
      signup_tag: this.dataset.klaviyoSignupTag || 'welcome-popup-signup',
    };
    if (country) {
      properties.country = country;
    }
    const profileAttributes = {
      email,
      properties,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-04-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Welcome popup',
              profile: { data: { type: 'profile', attributes: profileAttributes } },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      }
    );

    console.debug('[WelcomePopup] Klaviyo response', response.status);

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Klaviyo ${response.status}: ${text}`);
    }
  }

  // Identify the browser (Klaviyo _learnq queue) so onsite events attribute to this
  // profile. Best-effort: the subscription already succeeded by this point.
  #identifyInKlaviyo(email) {
    if (!email) return;
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { '$email': email }]);
    } catch (err) {
      console.warn('[WelcomePopup] klaviyo identify failed', err);
    }
  }

  #goToConfirmation() {
    this.#currentStep = 2;
    this.classList.add('welcome-popup--step-2');
    const stepConfirmation = this.querySelector('[ref="stepConfirmation"]');
    const stepForm = this.querySelector('[ref="stepForm"]');
    if (stepForm) stepForm.setAttribute('hidden', '');
    if (stepConfirmation) {
      stepConfirmation.removeAttribute('hidden');
      const heading = stepConfirmation.querySelector('#WelcomePopupConfirmHeading');
      requestAnimationFrame(() => heading?.focus());
    }
  }

  // State persistence --------------------------------------------

  #persistDismissal() {
    const frequency = this.dataset.frequency || 'session';
    if (frequency === 'session') {
      try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch {}
      return;
    }
    const ms = FREQUENCY_MS[frequency];
    if (!ms) return;
    try {
      const state = this.#readState();
      state.suppressUntil = new Date(Date.now() + ms).toISOString();
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {}
  }

  #persistSignup() {
    try {
      const state = this.#readState();
      state.signedUp = true;
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {}
  }

  #readState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      if (!raw) return { suppressUntil: null, signedUp: false };
      const parsed = JSON.parse(raw);
      return {
        suppressUntil: parsed?.suppressUntil ?? null,
        signedUp: Boolean(parsed?.signedUp),
      };
    } catch {
      return { suppressUntil: null, signedUp: false };
    }
  }

  #isSessionDismissed() {
    try { return sessionStorage.getItem(SESSION_KEY) === 'true'; } catch { return false; }
  }

  #isPersistentlySuppressed() {
    const state = this.#readState();
    if (state.signedUp) return true;
    if (state.suppressUntil && new Date(state.suppressUntil) > new Date()) return true;
    return false;
  }

  #shouldYieldToGeoPopup() {
    if (this.dataset.suppressWhenGeo !== 'true') return false;
    try {
      // If geo popup was already dismissed, don't yield — let welcome fire.
      const geoDismissed = localStorage.getItem(GEO_DISMISSED_KEY) === 'true';
      if (geoDismissed) return false;
      // Geo popup only fires for non-US visitors. If we're in US,
      // it will never fire, so don't yield (otherwise welcome is
      // permanently blocked waiting for an event that never happens).
      const visitorCountry = (this.dataset.countryCode || '').trim().toUpperCase();
      if (visitorCountry === 'US') return false;
      return true;
    } catch {
      return false;
    }
  }

  #isExcludedPath() {
    const current = normalizePath(window.location.pathname);
    return this.#excludedPaths.some((pattern) => pathMatches(pattern, current));
  }

  get #excludedPaths() {
    return (this.dataset.excludedPaths || '')
      .split(/[\n,]+/)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  // Suppresses for this pageview only when the visitor lands on a deep link such as
  // /pages/our-founder#carrie-video. Nothing is persisted, so the popup fires as
  // usual on the next page they visit.
  #isExcludedHash() {
    const current = normalizeHash(window.location.hash);
    if (!current) return false;
    return this.#excludedHashes.some((pattern) => hashMatches(pattern, current));
  }

  get #excludedHashes() {
    return (this.dataset.excludedHashes || '')
      .split(/[\n,]+/)
      .map((h) => h.trim())
      .filter(Boolean);
  }

  get isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  // Form state ----------------------------------------------------

  #setFormState(state) {
    if (this.#form) this.#form.dataset.state = state;
  }

  #showError(message) {
    if (this.#errorEl) {
      this.#errorEl.textContent = message;
      this.#errorEl.classList.remove('hidden');
    }
    this.#emailInput?.setAttribute('aria-invalid', 'true');
  }

  #clearError() {
    if (this.#errorEl) {
      this.#errorEl.textContent = '';
      this.#errorEl.classList.add('hidden');
    }
    this.#emailInput?.removeAttribute('aria-invalid');
    if (this.#form?.dataset.state !== 'success') this.#setFormState('idle');
  }

  // DOM accessors -------------------------------------------------

  get #form() { return this.querySelector('.welcome-popup__form'); }
  get #emailInput() { return this.#form?.querySelector('[name="contact[email]"]') ?? null; }
  get #submitButton() { return this.#form?.querySelector('.welcome-popup__submit') ?? null; }
  get #errorEl() { return this.#form?.querySelector('.welcome-popup__form-message--error') ?? null; }
  get #closeButton() { return this.querySelector('.welcome-popup__close'); }
  get #errorInvalid() { return this.#form?.dataset.errorInvalidEmail || 'Enter a valid email address.'; }
  get #errorRequired() { return this.#form?.dataset.errorRequired || 'Enter your email address.'; }

  // GA4 -----------------------------------------------------------

  #emitGA4(eventName, extras = {}) {
    const payload = {
      popup_id: this.dataset.ga4PopupId || 'welcome_popup_default',
      step: this.#currentStep,
      ...extras,
    };
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...payload });
    }
  }
}

function normalizePath(path) {
  if (!path) return '/';
  let n = path.trim();
  if (!n.startsWith('/')) n = `/${n}`;
  if (n.length > 1 && n.endsWith('/')) n = n.slice(0, -1);
  return n;
}

function pathMatches(pattern, currentPath) {
  const p = normalizePath(pattern);
  if (p.endsWith('*')) return currentPath.startsWith(p.slice(0, -1));
  return currentPath === p;
}

function normalizeHash(hash) {
  return (hash || '').trim().replace(/^#+/, '').toLowerCase();
}

function hashMatches(pattern, currentHash) {
  const p = normalizeHash(pattern);
  if (!p) return false;
  if (p.endsWith('*')) return currentHash.startsWith(p.slice(0, -1));
  return currentHash === p;
}

if (!customElements.get('welcome-popup-component')) {
  customElements.define('welcome-popup-component', WelcomePopupComponent);
}
